import React, { useState, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { ShieldAlert, ShieldOff, Shield, Users, Smartphone, Wifi, MemoryStick, MapPin, RefreshCw, Wallet, IndianRupee, CheckCircle, Clock, ClipboardList, Check, X, Trash, UserPlus, Eye, EyeOff, Bug, Pencil, ChevronDown, ChevronUp, ExternalLink, List, Activity, Search } from 'lucide-react';
import { apiClient } from '../utils/api';
import { API } from '../utils/constants';
import './AdminPage.css';

function DeviceTierBadge({ tier }) {
    if (!tier) return null;
    const map = {
        low: { label: 'Low-end', class: 'low-end' },
        mid: { label: 'Mid-range', class: 'flagship' }, // mid and high often get same treatment in request
        high: { label: 'Flagship', class: 'flagship' },
    };
    const t = map[tier] || { label: tier, class: 'wifi' };
    return (
        <span className={`admin-chip ${t.class}`}>
            {t.label}
        </span>
    );
}

function BlobVideoPlayer({ src }) {
    const [blobUrl, setBlobUrl] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        let url = src;
        if (!url.startsWith('http')) {
            const base = API.replace(/\/api\/?$/, '');
            url = base + url;
        }

        let current = true;
        setLoading(true);
        setError(null);

        apiClient.getVideoBlobUrl(url)
            .then(blobUrl => {
                if (current) {
                    setBlobUrl(blobUrl);
                    setLoading(false);
                }
            })
            .catch(err => {
                if (current) {
                    console.error('Video error:', err);
                    setError(err.message);
                    setLoading(false);
                }
            });

        return () => {
            current = false;
            if (blobUrl) URL.revokeObjectURL(blobUrl);
        };
    }, [src]);

    if (loading) return (
        <div style={{ height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', background: '#111', color: '#666' }}>
            <div className="spinner" style={{ marginBottom: '12px' }} />
            <div style={{ fontSize: '0.8rem' }}>Streaming recording...</div>
        </div>
    );

    if (error) return (
        <div style={{ height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', background: '#111', color: '#ff4d4d', padding: '20px', textAlign: 'center' }}>
            <ShieldAlert size={32} style={{ marginBottom: '12px', opacity: 0.5 }} />
            <div style={{ fontSize: '0.85rem' }}>Failed to load recording</div>
            <div style={{ fontSize: '0.7rem', opacity: 0.7, marginTop: '4px' }}>{error}</div>
        </div>
    );

    return (
        <video
            src={blobUrl}
            controls
            style={{ width: '100%', height: '100%', objectFit: 'contain' }}
            autoPlay={false}
        />
    );
}

export default function AdminPage({ company, showToast, onImpersonate }) {
    const [mainTab, setMainTab] = useState('tests'); // 'tests' | 'bugs' | 'testers' | 'payments' | 'notifications'

    // ── Notification state ──
    const [notifTitle, setNotifTitle] = useState('');
    const [notifBody, setNotifBody] = useState('');
    const [notifTestId, setNotifTestId] = useState('');
    const [sendingNotif, setSendingNotif] = useState(false);
    const [notifResult, setNotifResult] = useState(null);
    const [togglingNotifId, setTogglingNotifId] = useState(null);


    // ── Tests state ──
    const [allTests, setAllTests] = useState([]);
    const [loadingTests, setLoadingTests] = useState(false);
    const [expandedTest, setExpandedTest] = useState(null);

    // Assign testers modal state
    const [assignModalOpen, setAssignModalOpen] = useState(false);
    const [assignTestContext, setAssignTestContext] = useState(null);
    const [assignTargetTesters, setAssignTargetTesters] = useState([]);
    const [assigningLoading, setAssigningLoading] = useState(false);

    // ── Testers state ──
    const [testers, setTesters] = useState([]);
    const [loadingTesters, setLoadingTesters] = useState(true);
    const [banningId, setBanningId] = useState(null);
    const [filter, setFilter] = useState('all');

    // ── Payments state ──
    const [pending, setPending] = useState([]);
    const [loadingPending, setLoadingPending] = useState(false);
    const [paying, setPaying] = useState(false);
    const [selected, setSelected] = useState(new Set()); // tester ids selected for payment

    // ── Bug Approval state ──
    const [pendingBugs, setPendingBugs] = useState([]);
    const [loadingBugs, setLoadingBugs] = useState(false);
    const [approvingBugId, setApprovingBugId] = useState(null);
    const [expandedBugId, setExpandedBugId] = useState(null);
    const [rerunningBugId, setRerunningBugId] = useState(null);

    // ── Active Sessions state ──
    const [activeSessions, setActiveSessions] = useState([]);
    const [loadingSessions, setLoadingSessions] = useState(false);
    const [closingSessionId, setClosingSessionId] = useState(null);

    // Budget editing state
    const [editingBudget, setEditingBudget] = useState(null); // { id, total_budget, price_paid, tester_quota, testing_iterations }
    const [savingBudget, setSavingBudget] = useState(false);

    // Assignment search
    const [assignSearch, setAssignSearch] = useState('');

    // --- AI Terminal State ---
    const [terminalBugId, setTerminalBugId] = useState(null);
    const [terminalLogs, setTerminalLogs] = useState('');
    const [terminalLoading, setTerminalLoading] = useState(false);

    const loadTesters = useCallback(async () => {
        try {
            setLoadingTesters(true);
            const data = await apiClient.getAllTesters();
            setTesters(Array.isArray(data) ? data : []);
        } catch (err) {
            showToast('Failed to load testers: ' + err.message, 'error');
        } finally {
            setLoadingTesters(false);
        }
    }, [showToast]);

    const loadPending = useCallback(async () => {
        try {
            setLoadingPending(true);
            const data = await apiClient.getPendingPayments();
            setPending(data.testers || []);
        } catch (err) {
            showToast('Failed to load pending payments: ' + err.message, 'error');
        } finally {
            setLoadingPending(false);
        }
    }, [showToast]);

    const loadAdminTests = useCallback(async () => {
        try {
            setLoadingTests(true);
            const data = await apiClient.getAdminTests();
            setAllTests(Array.isArray(data) ? data : []);
        } catch (err) {
            showToast('Failed to load tests: ' + err.message, 'error');
        } finally {
            setLoadingTests(false);
        }
    }, [showToast]);

    const loadPendingBugs = useCallback(async () => {
        try {
            setLoadingBugs(true);
            const data = await apiClient.adminGetPendingBugs();
            setPendingBugs(Array.isArray(data) ? data : []);
        } catch (err) {
            showToast('Failed to load pending bugs: ' + err.message, 'error');
        } finally {
            setLoadingBugs(false);
        }
    }, [showToast]);

    const loadSessions = useCallback(async () => {
        try {
            setLoadingSessions(true);
            const data = await apiClient.getAdminActiveSessions();
            setActiveSessions(data.sessions || []);
        } catch (err) {
            showToast('Failed to load active sessions: ' + err.message, 'error');
        } finally {
            setLoadingSessions(false);
        }
    }, [showToast]);

    useEffect(() => { loadTesters(); }, [loadTesters]);
    useEffect(() => { if (mainTab === 'payments') loadPending(); }, [mainTab, loadPending]);
    useEffect(() => { if (mainTab === 'tests' || mainTab === 'notifications') loadAdminTests(); }, [mainTab, loadAdminTests]);
    useEffect(() => { if (mainTab === 'bugs') loadPendingBugs(); }, [mainTab, loadPendingBugs]);
    useEffect(() => { if (mainTab === 'sessions') loadSessions(); }, [mainTab, loadSessions]);

    // AI Terminal polling
    useEffect(() => {
        if (!terminalBugId) return;

        let interval;
        const poll = async () => {
            try {
                setTerminalLoading(true);
                const data = await apiClient.getBugLogs(terminalBugId);
                setTerminalLogs(data.logs || 'No logs available yet...');
            } catch (err) {
                console.error('Log poll error:', err);
            } finally {
                setTerminalLoading(false);
            }
        };

        poll(); // initial check
        interval = setInterval(poll, 3000); // every 3s

        return () => clearInterval(interval);
    }, [terminalBugId]);

    const handleSendNotification = async () => {
        if (!notifTitle.trim() || !notifBody.trim()) { showToast('Title and body are required', 'error'); return; }
        setSendingNotif(true); setNotifResult(null);
        try {
            const payload = { title: notifTitle, body: notifBody };
            if (notifTestId) payload.test_id = parseInt(notifTestId);
            const result = await apiClient.sendAdminNotification(payload);
            setNotifResult(result);
            showToast(`✅ Sent to ${result.sent} testers!`);
        } catch (err) {
            showToast('Failed: ' + err.message, 'error');
        } finally {
            setSendingNotif(false);
        }
    };

    const handleToggleNotifications = async (tester) => {
        setTogglingNotifId(tester.id);
        try {
            await apiClient.toggleTesterNotifications(tester.id, !(tester.notifications_enabled !== false));
            showToast(`🔔 Notifications ${tester.notifications_enabled !== false ? 'disabled' : 'enabled'} for ${tester.full_name}`);
            loadTesters();
        } catch (err) {
            showToast('Failed: ' + err.message, 'error');
        } finally {
            setTogglingNotifId(null);
        }
    };


    const handleBan = async (tester) => {
        const reason = window.prompt(`Ban reason for ${tester.full_name}:`, 'Violation of terms of service');
        if (reason === null) return;
        setBanningId(tester.id);
        try {
            await apiClient.banTester(tester.id, reason);
            showToast(`🚫 ${tester.full_name} has been banned`);
            loadTesters();
        } catch (err) {
            showToast('Failed to ban tester: ' + err.message, 'error');
        } finally {
            setBanningId(null);
        }
    };

    const handleUnban = async (tester) => {
        if (!window.confirm(`Unban ${tester.full_name}?`)) return;
        setBanningId(tester.id);
        try {
            await apiClient.unbanTester(tester.id);
            showToast(`✅ ${tester.full_name} has been unbanned`);
            loadTesters();
        } catch (err) {
            showToast('Failed to unban tester: ' + err.message, 'error');
        } finally {
            setBanningId(null);
        }
    };

    const handleDeleteBug = async (bugId) => {
        if (!window.confirm("Permanently delete this bug report? This cannot be undone.")) return;
        try {
            await apiClient.adminDeleteBug(bugId);
            showToast('🗑️ Bug report deleted');
            loadPendingBugs();
        } catch (err) {
            showToast('Error: ' + err.message, 'error');
        }
    };

    const handleDeleteCompany = async (companyId, companyName) => {
        if (!window.confirm(`WARNING: Permanently delete company "${companyName}"? This removes ALL their tests, bug reports, and invoices. This cannot be undone.`)) return;
        const confirmText = window.prompt('Type "DELETE" to confirm:');
        if (confirmText !== 'DELETE') {
            if (confirmText !== null) showToast('Cancelled: did not type DELETE', 'error');
            return;
        }
        try {
            await apiClient.adminDeleteCompany(companyId);
            showToast(`🗑️ Company ${companyName} deleted forever`);
            await loadAdminTests();
        } catch (err) {
            showToast('Error: ' + err.message, 'error');
        }
    };

    const handleBatchPay = async (ids = []) => {
        const total = ids.length === 0
            ? pending.reduce((s, t) => s + parseFloat(t.pending || 0), 0)
            : pending.filter(t => ids.includes(t.id)).reduce((s, t) => s + parseFloat(t.pending || 0), 0);
        const label = ids.length === 0 ? `all ${pending.length} testers` : `${ids.length} selected tester(s)`;
        if (!window.confirm(`Pay ₹${total.toFixed(2)} to ${label}? This cannot be undone.`)) return;
        setPaying(true);
        try {
            const result = await apiClient.batchPay(ids);
            showToast(`✅ ${result.message}`);
            setSelected(new Set());
            loadPending();
        } catch (err) {
            showToast('Payment failed: ' + err.message, 'error');
        } finally {
            setPaying(false);
        }
    };

    const toggleSelect = (id) => {
        setSelected(prev => {
            const n = new Set(prev);
            n.has(id) ? n.delete(id) : n.add(id);
            return n;
        });
    };

    const toggleAll = () => {
        setSelected(prev => prev.size === pending.length ? new Set() : new Set(pending.map(t => t.id)));
    };

    const filtered = testers.filter(t => {
        if (filter === 'banned') return t.is_banned;
        if (filter === 'active') return !t.is_banned;
        return true;
    });

    const bannedCount = testers.filter(t => t.is_banned).length;
    const totalPending = pending.reduce((s, t) => s + parseFloat(t.pending || 0), 0);

    return (
        <div className="admin-page">
            <header className="admin-header-container">
                <h1>Admin</h1>
                <button
                    className="admin-refresh-btn"
                    onClick={async () => {
                        if (mainTab === 'testers') await loadTesters();
                        else if (mainTab === 'payments') await loadPending();
                        else if (mainTab === 'bugs') await loadPendingBugs();
                        else if (mainTab === 'sessions') await loadSessions();
                        else await loadAdminTests();
                    }}
                    title="Refresh"
                >
                    <RefreshCw size={18} />
                </button>
            </header>

            {/* Main tabs */}
            <div className="admin-tabs-row">
                <div className="admin-tabs-list">
                    <button className={`admin-tab ${mainTab === 'tests' ? 'active' : ''}`} onClick={() => setMainTab('tests')}>
                        All Tests
                    </button>
                    <button className={`admin-tab ${mainTab === 'bugs' ? 'active' : ''}`} onClick={() => setMainTab('bugs')}>
                        Bug Approval
                        {pendingBugs.length > 0 && <span className="admin-tab-count">{pendingBugs.length}</span>}
                    </button>
                    <button className={`admin-tab ${mainTab === 'testers' ? 'active' : ''}`} onClick={() => setMainTab('testers')}>
                        Testers
                    </button>
                    <button className={`admin-tab ${mainTab === 'payments' ? 'active' : ''}`} onClick={() => setMainTab('payments')}>
                        Payments
                    </button>
                    <button className={`admin-tab ${mainTab === 'sessions' ? 'active' : ''}`} onClick={() => setMainTab('sessions')}>
                        Active Sessions
                    </button>
                    <button className={`admin-tab ${mainTab === 'notifications' ? 'active' : ''}`} onClick={() => setMainTab('notifications')}>
                        Push Notifications
                    </button>
                </div>
            </div>

            <div className="admin-content">
                {/* ════ TESTERS TAB ════ */}
                {mainTab === 'testers' && (
                    <>
                        <div className="admin-stats-row">
                            {[
                                { label: 'Total Testers', value: testers.length, icon: Users, color: '#60a5fa' },
                                { label: 'Active', value: testers.length - bannedCount, icon: Shield, color: '#a78bfa' },
                                { label: 'Banned', value: bannedCount, icon: ShieldOff, color: '#f87171' },
                            ].map((s, i) => (
                                <div key={i} className="admin-stat-card">
                                    <div className="admin-stat-icon-box" style={{ color: s.color, borderColor: s.color + '40', background: s.color + '10' }}>
                                        <s.icon size={20} />
                                    </div>
                                    <div>
                                        <div className="admin-stat-val">{s.value}</div>
                                        <div className="admin-stat-label">{s.label}</div>
                                    </div>
                                </div>
                            ))}
                        </div>

                        <div className="admin-filter-tabs-container">
                            <div className="admin-filter-tabs">
                                {['all', 'active', 'banned'].map(f => (
                                    <button key={f} className={`admin-filter-tab ${filter === f ? 'active' : ''}`} onClick={() => setFilter(f)}>
                                        {f.charAt(0).toUpperCase() + f.slice(1)}
                                        {f === 'banned' && bannedCount > 0 && <span className="admin-filter-count">{bannedCount}</span>}
                                    </button>
                                ))}
                            </div>
                            <div className="admin-action-legend">
                                <span className="action-item"><ShieldOff size={12} /> Ban: Restrict Access</span>
                                <span className="action-item"><Trash size={12} /> Delete: Wipe Data</span>
                            </div>
                        </div>

                        {loadingTesters && <div className="loading-state"><div className="spinner" /><p>Loading testers…</p></div>}
                        {!loadingTesters && filtered.length === 0 && (
                            <div className="empty-state">
                                <div className="empty-icon">👥</div>
                                <h3>No testers found</h3>
                                <p>{filter === 'banned' ? 'No banned testers.' : 'No testers registered yet.'}</p>
                            </div>
                        )}
                        {!loadingTesters && filtered.length > 0 && (
                            <div className="admin-table-wrap">
                                <table className="admin-table">
                                    <thead>
                                        <tr>
                                            <th>Tester</th>
                                            <th>Device</th>
                                            <th>Network</th>
                                            <th>Ram</th>
                                            <th>Location</th>
                                            <th>Tests</th>
                                            <th>Status</th>
                                            <th>Action</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {filtered.map(tester => (
                                            <tr key={tester.id} className={tester.is_banned ? 'admin-row-banned' : ''}>
                                                <td>
                                                    <div className="admin-tester-name">{tester.full_name || '—'}</div>
                                                    <div className="admin-tester-email">{tester.email || tester.phone || '—'}</div>
                                                    {tester.is_banned && tester.ban_reason && (
                                                        <div className="admin-ban-reason">🚫 {tester.ban_reason}</div>
                                                    )}
                                                </td>
                                                <td>
                                                    <div className="admin-cell-text">{tester.device_model || '—'}</div>
                                                    <DeviceTierBadge tier={tester.device_tier} />
                                                </td>
                                                <td className="admin-cell-mono">{tester.network_type ? tester.network_type.toUpperCase() : '—'}</td>
                                                <td className="admin-cell-mono">{tester.ram_gb ? `${tester.ram_gb} GB` : '—'}</td>
                                                <td>
                                                    <div className="admin-cell-text">{tester.city || '—'}</div>
                                                    <div className="admin-tester-email">{tester.state || ''}</div>
                                                </td>
                                                <td className="admin-cell-mono">{tester.total_tests || 0}</td>
                                                <td>
                                                    {tester.is_banned
                                                        ? <span className="admin-status-badge banned">Banned</span>
                                                        : <span className="admin-status-badge active">Active</span>
                                                    }
                                                </td>
                                                <td>
                                                    <div className="admin-action-group">
                                                        {tester.is_banned ? (
                                                            <button className="admin-ghost-icon-btn" disabled={banningId === tester.id} onClick={() => handleUnban(tester)} title="Unban">
                                                                {banningId === tester.id ? <div className="spinner-sm" /> : <Shield size={14} />}
                                                            </button>
                                                        ) : (
                                                            <button className="admin-ghost-icon-btn" disabled={banningId === tester.id} onClick={() => handleBan(tester)} title="Ban">
                                                                {banningId === tester.id ? <div className="spinner-sm" /> : <ShieldOff size={14} />}
                                                            </button>
                                                        )}
                                                        <button className="admin-ghost-icon-btn delete" onClick={async () => {
                                                            if (!window.confirm(`WARNING: Permanently delete tester "${tester.full_name}"? This removes ALL their bug reports, earnings, and payment history. This cannot be undone.`)) return;
                                                            const confirmText = window.prompt('Type "DELETE" to confirm:');
                                                            if (confirmText !== 'DELETE') { if (confirmText !== null) showToast('Cancelled: did not type DELETE', 'error'); return; }
                                                            try {
                                                                await apiClient.adminDeleteTester(tester.id);
                                                                showToast(`🗑️ Tester ${tester.full_name} deleted`);
                                                                loadTesters();
                                                            } catch (err) { showToast('Error: ' + err.message, 'error'); }
                                                        }} title="Permanently Delete Tester">
                                                            <Trash size={14} />
                                                        </button>
                                                    </div>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </>
                )}

                {/* ════ NOTIFICATIONS TAB ════ */}
                {mainTab === 'notifications' && (
                    <>
                        {/* Manual Send Form */}
                        <div className="admin-notif-form">
                            <h3 className="admin-notif-header">
                                Send Manual Push Notification
                            </h3>
                            <div className="admin-notif-grid">
                                <div className="admin-form-group">
                                    <label>Title</label>
                                    <input
                                        type="text"
                                        className="admin-input"
                                        value={notifTitle}
                                        onChange={e => setNotifTitle(e.target.value)}
                                        placeholder="e.g. New Testing Opportunity!"
                                    />
                                </div>
                                <div className="admin-form-group">
                                    <label>Target Test ID (optional)</label>
                                    <input
                                        type="number"
                                        className="admin-input"
                                        value={notifTestId}
                                        onChange={e => setNotifTestId(e.target.value)}
                                        placeholder="e.g. 42"
                                    />
                                </div>
                            </div>
                            <div className="admin-form-group">
                                <label>Message Body</label>
                                <textarea
                                    className="admin-textarea"
                                    rows={3}
                                    value={notifBody}
                                    onChange={e => setNotifBody(e.target.value)}
                                    placeholder="Describe the opportunity: app name, pay amount, etc."
                                />
                            </div>
                            <div className="admin-notif-actions">
                                <button
                                    className="admin-send-btn"
                                    disabled={sendingNotif || !notifTitle || !notifBody}
                                    onClick={handleSendNotification}
                                >
                                    {sendingNotif ? <div className="spinner-sm" /> : 'Send Notification'}
                                </button>
                                {notifResult && (
                                    <span className="admin-notif-result">
                                        Sent: {notifResult.sent} / {notifResult.total}
                                    </span>
                                )}
                            </div>
                            <div className="admin-schedule-box">
                                Automatic schedule: Notifications fire automatically at <strong>9:00 AM IST</strong> and <strong>3:00 PM IST</strong> every day when there are active tests. Only testers with FCM tokens and notifications enabled receive them.
                            </div>
                        </div>

                        {/* Per-tester notification toggle */}
                        <div className="admin-table-wrap glass-card">
                            <div style={{ padding: '16px 20px', borderBottom: '1px solid rgba(255,255,255,0.06)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <h3 style={{ margin: 0 }}>Tester Notification Settings</h3>
                                <span style={{ fontSize: '0.8rem', color: '#888' }}>{testers.filter(t => t.notifications_enabled !== false && t.fcm_token).length} / {testers.length} have notifications active</span>
                            </div>
                            {loadingTesters ? <div className="loading-state"><div className="spinner" /><p>Loading…</p></div> : (
                                <table className="admin-table">
                                    <thead>
                                        <tr>
                                            <th>Tester</th>
                                            <th>Device</th>
                                            <th>Fcm token</th>
                                            <th>Notifications</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {testers.map(tester => (
                                            <tr key={tester.id}>
                                                <td>
                                                    <div className="admin-tester-name">{tester.full_name}</div>
                                                    <div className="admin-tester-email">{tester.email || tester.phone}</div>
                                                </td>
                                                <td>{tester.device_model || '—'}</td>
                                                <td>
                                                    {tester.fcm_token
                                                        ? <span className="admin-status-badge active" style={{ fontSize: '11px' }}>Registered</span>
                                                        : <span className="admin-status-badge no-token">No token</span>
                                                    }
                                                </td>
                                                <td>
                                                    <button
                                                        className={`admin-status-badge ${tester.notifications_enabled !== false ? 'on' : 'off'}`}
                                                        disabled={togglingNotifId === tester.id}
                                                        onClick={() => handleToggleNotifications(tester)}
                                                    >
                                                        {togglingNotifId === tester.id
                                                            ? <div className="spinner-sm" />
                                                            : tester.notifications_enabled !== false ? 'On' : 'Off'
                                                        }
                                                    </button>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            )}
                        </div>
                    </>
                )}


                {/* ════ PAYMENTS TAB ════ */}
                {mainTab === 'payments' && (
                    <>
                        <div className="admin-stats-row">
                            {[
                                { label: 'Awaiting payment', value: pending.length },
                                { label: 'Total pending (₹)', value: totalPending.toLocaleString('en-IN', { maximumFractionDigits: 2 }) },
                                { label: 'Next payout', value: 'Sunday 12 PM' },
                            ].map((s, i) => (
                                <div key={i} className="admin-stat-small">
                                    <div className="admin-stat-label">{s.label}</div>
                                    <div className="admin-stat-val">{i === 1 && '₹'}{s.value}</div>
                                    <div className="admin-stat-desc">{i === 1 ? 'Pending disbursement' : ''}</div>
                                </div>
                            ))}
                        </div>

                        <div className="admin-payments-toolbar">
                            <div style={{ display: 'flex', gap: 12 }}>
                                <button className="admin-ghost-btn" disabled={paying || pending.length === 0} onClick={() => handleBatchPay([])}>
                                    {paying ? <div className="spinner-sm" /> : `Pay all (${pending.length})`}
                                </button>
                                <button className="admin-ghost-btn" onClick={() => {
                                    window.open('/api/admin/payments/csv', '_blank');
                                }}>
                                    Export CSV
                                </button>
                            </div>
                        </div>

                        {loadingPending && <div className="loading-state"><div className="spinner" /><p>Loading pending payments…</p></div>}
                        {!loadingPending && pending.length === 0 && (
                            <div className="admin-empty-state">
                                <span className="empty-dash">—</span>
                                <div className="empty-text">No pending payments</div>
                            </div>
                        )}
                        {!loadingPending && pending.length > 0 && (
                            <div className="admin-table-wrap glass-card">
                                <table className="admin-table">
                                    <thead>
                                        <tr>
                                            <th><input type="checkbox" checked={selected.size === pending.length} onChange={toggleAll} /></th>
                                            <th>Tester</th>
                                            <th>UPI ID</th>
                                            <th>Tests Done</th>
                                            <th>Total Earned</th>
                                            <th>Already Paid</th>
                                            <th>Pending</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {pending.map(t => (
                                            <tr key={t.id} className={selected.has(t.id) ? 'admin-row-selected' : ''}>
                                                <td data-label="Select"><input type="checkbox" checked={selected.has(t.id)} onChange={() => toggleSelect(t.id)} /></td>
                                                <td>
                                                    <div className="admin-tester-name">{t.full_name}</div>
                                                    <div className="admin-tester-email">{t.phone}</div>
                                                </td>
                                                <td data-label="UPI ID">
                                                    {t.upi_id
                                                        ? <span className="admin-upi-chip">{t.upi_id}</span>
                                                        : <span className="admin-upi-missing">⚠️ No UPI</span>
                                                    }
                                                </td>
                                                <td data-label="Tests Done">{t.total_tests}</td>
                                                <td data-label="Total Earned">₹{parseFloat(t.total_earnings).toFixed(2)}</td>
                                                <td data-label="Already Paid">₹{parseFloat(t.total_paid).toFixed(2)}</td>
                                                <td data-label="Pending"><span className="admin-pending-amount">₹{parseFloat(t.pending).toFixed(2)}</span></td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </>
                )}

                {/* ════ TESTS TAB ════ */}
                {mainTab === 'tests' && (
                    <>
                        {loadingTests && <div className="loading-state"><div className="spinner" /><p>Loading tests…</p></div>}
                        {!loadingTests && allTests.length === 0 && (
                            <div className="empty-state glass-card">
                                <div className="empty-icon">📝</div>
                                <h3>No Tests</h3>
                                <p>No company has created a test yet.</p>
                            </div>
                        )}
                        {!loadingTests && allTests.length > 0 && (
                            <div className="admin-table-wrap glass-card">
                                <table className="admin-table">
                                    <thead>
                                        <tr>
                                            <th>Company / app</th>
                                            <th>Requested testers</th>
                                            <th>Iterations</th>
                                            <th>Budget / payout</th>
                                            <th>Status</th>
                                            <th>Action</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {allTests.map(test => (
                                            <React.Fragment key={test.id}>
                                                <tr>
                                                    <td>
                                                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                                            <button
                                                                className="icon-btn"
                                                                onClick={() => setExpandedTest(expandedTest === test.id ? null : test.id)}
                                                                style={{ padding: 4 }}
                                                            >
                                                                {expandedTest === test.id ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                                                            </button>
                                                            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                                                {test.company_logo ? (
                                                                    <img src={test.company_logo} alt={test.company_name} style={{ width: 32, height: 32, borderRadius: '50%' }} />
                                                                ) : (
                                                                    <div style={{ width: 32, height: 32, borderRadius: '50%', background: '#333', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                                                        <Users size={16} color="#888" />
                                                                    </div>
                                                                )}
                                                                <div>
                                                                    <div className="admin-tester-name">{test.company_name}</div>
                                                                    <div className="admin-tester-email">App: {test.app_name}</div>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </td>
                                                    <td data-label="Testers">{test.tester_quota || 20}</td>
                                                    <td data-label="Iterations">{test.testing_iterations || 1}</td>
                                                    <td data-label="Budget/Payout">
                                                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                                            <div className="admin-budget-display">
                                                                <div className="admin-budget-total">₹{Number(test.total_budget || 0).toLocaleString()}</div>
                                                                <div className="admin-budget-per">₹{Number(test.price_paid || 0).toFixed(2)}/test</div>
                                                            </div>
                                                            <button
                                                                className="admin-edit-btn"
                                                                onClick={() => {
                                                                    setEditingBudget({
                                                                        id: test.id,
                                                                        total_budget: test.total_budget || 0,
                                                                        price_paid: test.price_paid || 0,
                                                                        tester_quota: test.tester_quota || 20,
                                                                        testing_iterations: test.testing_iterations || 1,
                                                                        status: test.status,
                                                                        app_name: test.app_name
                                                                    });
                                                                }}
                                                            >
                                                                <Pencil size={11} />
                                                            </button>
                                                        </div>
                                                    </td>
                                                    <td data-label="Status">
                                                        <span className={`admin-status-badge ${test.status === 'approved' || test.status === 'active' ? 'active' : test.status === 'rejected' ? 'rejected' : 'pending'}`}>
                                                            {test.status === 'active' ? 'Active' : (test.status || 'pending')}
                                                        </span>
                                                    </td>
                                                    <td style={{ display: 'flex', gap: '8px' }} data-label="Action">
                                                        <button className="admin-ghost-icon-btn" onClick={() => setExpandedTest(expandedTest === test.id ? null : test.id)} title="View details">
                                                            <Eye size={14} />
                                                        </button>
                                                        <button className="admin-ghost-icon-btn" onClick={() => {
                                                            setEditingBudget({
                                                                id: test.id,
                                                                total_budget: test.total_budget || 0,
                                                                price_paid: test.price_paid || 0,
                                                                tester_quota: test.tester_quota || 20,
                                                                testing_iterations: test.testing_iterations || 1,
                                                                status: test.status,
                                                                app_name: test.app_name
                                                            });
                                                        }} title="Edit/Approve">
                                                            <Pencil size={14} />
                                                        </button>
                                                        <button className="admin-ghost-icon-btn" onClick={() => {
                                                            setAssignTestContext(test);
                                                            setAssignTargetTesters([]);
                                                            setAssignSearch('');
                                                            setAssignModalOpen(true);
                                                            if (testers.length === 0) loadTesters();
                                                        }} title="Assign testers">
                                                            <UserPlus size={14} />
                                                        </button>
                                                        <button className="admin-ghost-icon-btn" onClick={() => onImpersonate(test.company_id)} title="Impersonate Company">
                                                            <Shield size={14} />
                                                        </button>
                                                        <button className="admin-ghost-icon-btn delete" onClick={async () => {
                                                            const confirm1 = window.confirm(`WARNING: Are you sure you want to delete this test (${test.app_name})? This deletes all associated bugs, AI analysis, and tester progress.`);
                                                            if (!confirm1) return;
                                                            const confirmText = window.prompt(`To permanently delete this test, type "DELETE" below:`);
                                                            if (confirmText === 'DELETE') {
                                                                try {
                                                                    await apiClient.deleteTest(test.id);
                                                                    showToast(`🗑️ Test deleted`);
                                                                    await loadAdminTests();
                                                                } catch (err) { showToast('Error: ' + err.message, 'error'); }
                                                            }
                                                        }} title="Delete Test">
                                                            <Trash size={14} />
                                                        </button>
                                                    </td>
                                                </tr>
                                                {expandedTest === test.id && (
                                                    <tr className="admin-expanded-row">
                                                        <td colSpan="7">
                                                            <div className="admin-expanded-content" style={{ padding: '20px', background: 'rgba(255,255,255,0.02)', borderRadius: '8px' }}>
                                                                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '32px' }}>
                                                                    <div>
                                                                        <h4 style={{ marginBottom: '12px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                                                                            <List size={16} /> Instructions
                                                                        </h4>
                                                                        <pre style={{ whiteSpace: 'pre-wrap', fontSize: '0.85rem', color: '#ccc', background: 'rgba(0,0,0,0.2)', padding: '12px', borderRadius: '4px' }}>
                                                                            {test.instructions}
                                                                        </pre>
                                                                    </div>
                                                                    <div>
                                                                        <h4 style={{ marginBottom: '12px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                                                                            <Smartphone size={16} /> Build & Criteria
                                                                        </h4>
                                                                        <div style={{ marginBottom: '16px' }}>
                                                                            <div style={{ fontSize: '0.8rem', color: '#888', marginBottom: '4px' }}>APK Link:</div>
                                                                            {test.apk_file_url ? (
                                                                                <a href={test.apk_file_url} target="_blank" rel="noreferrer" style={{ color: 'var(--saffron)', display: 'flex', alignItems: 'center', gap: '4px' }}>
                                                                                    Download APK <ExternalLink size={12} />
                                                                                </a>
                                                                            ) : 'No APK uploaded'}
                                                                        </div>
                                                                        <div>
                                                                            <div style={{ fontSize: '0.8rem', color: '#888', marginBottom: '8px' }}>Targeting:</div>
                                                                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                                                                                <DeviceTierBadge tier={test.criteria?.device_tier} />
                                                                                {test.criteria?.network_type && <span className="admin-chip wifi">📶 {test.criteria.network_type.toUpperCase()}</span>}
                                                                                {(test.criteria?.max_ram_gb || test.criteria?.min_ram_gb) && (
                                                                                    <span className="admin-chip wifi">
                                                                                        💾 {test.criteria.min_ram_gb || 0}-{test.criteria.max_ram_gb || '∞'}GB RAM
                                                                                    </span>
                                                                                )}
                                                                            </div>
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        </td>
                                                    </tr>
                                                )}
                                            </React.Fragment>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </>
                )}

                {/* ════ SESSIONS TAB ════ */}
                {mainTab === 'sessions' && (
                    <>
                        <div className="admin-stats-row">
                            <div className="admin-stat-card glass-card">
                                <Activity size={22} color="#5f27cd" />
                                <div>
                                    <div className="admin-stat-val">{activeSessions.length}</div>
                                    <div className="admin-stat-label">Active Testing Sessions</div>
                                </div>
                            </div>
                        </div>

                        {loadingSessions && <div className="loading-state"><div className="spinner" /><p>Loading active sessions…</p></div>}
                        {!loadingSessions && activeSessions.length === 0 && (
                            <div className="empty-state glass-card">
                                <div className="empty-icon">🎮</div>
                                <h3>No Active Sessions</h3>
                                <p>No testers are currently in a manual testing session.</p>
                            </div>
                        )}
                        {!loadingSessions && activeSessions.length > 0 && (
                            <div className="admin-table-wrap glass-card">
                                <table className="admin-table">
                                    <thead>
                                        <tr>
                                            <th>Tester</th>
                                            <th>Target app</th>
                                            <th>Assigned at</th>
                                            <th>Action</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {activeSessions.map(session => (
                                            <tr key={session.session_id}>
                                                <td>
                                                    <div className="admin-tester-name">{session.full_name}</div>
                                                    <div className="admin-tester-email">{session.email}</div>
                                                </td>
                                                <td>
                                                    <div className="admin-tester-name">{session.app_name}</div>
                                                    <div className="admin-tester-email">by {session.company_name}</div>
                                                </td>
                                                <td>
                                                    <div style={{ fontSize: '0.85rem' }}>{new Date(session.started_at).toLocaleString()}</div>
                                                </td>
                                                <td>
                                                    <button
                                                        className="admin-ghost-icon-btn delete"
                                                        disabled={closingSessionId === session.session_id}
                                                        onClick={async () => {
                                                            if (!window.confirm(`Force close session for ${session.full_name}? This will free up the testing slot.`)) return;
                                                            setClosingSessionId(session.session_id);
                                                            try {
                                                                await apiClient.forceCloseSession(session.session_id);
                                                                showToast(`🚫 Session forcefully closed`);
                                                                loadSessions();
                                                            } catch (err) {
                                                                showToast('Error: ' + err.message, 'error');
                                                            } finally {
                                                                setClosingSessionId(null);
                                                            }
                                                        }}
                                                    >
                                                        {closingSessionId === session.session_id ? <div className="spinner-sm" /> : <X size={14} />}
                                                    </button>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </>
                )}

                {mainTab === 'bugs' && (
                    <>
                        {loadingBugs && <div className="loading-state"><div className="spinner" /><p>Loading pending bugs…</p></div>}
                        {!loadingBugs && pendingBugs.length === 0 && (
                            <div className="empty-state glass-card">
                                <div className="empty-icon">✅</div>
                                <h3>All Bugs Approved</h3>
                                <p>No bugs waiting for approval right now.</p>
                            </div>
                        )}
                        {!loadingBugs && pendingBugs.length > 0 && (
                            <div className="admin-table-wrap glass-card">
                                <table className="admin-table">
                                    <thead>
                                        <tr>
                                            <th>App / tester</th>
                                            <th>Issue</th>
                                            <th>Ai verdict</th>
                                            <th>Details</th>
                                            <th>Action</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {pendingBugs.map(bug => {
                                            const isExpanded = expandedBugId === bug.id;
                                            const aiVerdict = bug.ai_analysis && bug.ai_analysis.includes('FINAL VERDICT: [APPROVE]') ? 'APPROVE' :
                                                (bug.ai_analysis && bug.ai_analysis.includes('FINAL VERDICT: [REJECT]') ? 'REJECT' : 'PENDING');

                                            return (
                                                <React.Fragment key={bug.id}>
                                                    <tr className={isExpanded ? 'admin-row-expanded-header' : ''}>
                                                        <td>
                                                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                                                <button
                                                                    className="icon-btn"
                                                                    onClick={() => setExpandedBugId(isExpanded ? null : bug.id)}
                                                                    style={{ padding: 4 }}
                                                                >
                                                                    {isExpanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                                                                </button>
                                                                <div>
                                                                    <div className="admin-tester-name">{bug.app_name}</div>
                                                                    <div className="admin-tester-email">By: {bug.tester_name}</div>
                                                                </div>
                                                            </div>
                                                        </td>
                                                        <td data-label="Issue" style={{ maxWidth: 300 }}>
                                                            <div style={{ fontWeight: 600 }}>{bug.bug_title}</div>
                                                            <div style={{ fontSize: '0.85rem', color: '#888', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                                                {bug.bug_description}
                                                            </div>
                                                        </td>
                                                        <td data-label="Ai verdict">
                                                            {aiVerdict === 'APPROVE' && <span className="admin-status-badge active">Ai approve</span>}
                                                            {aiVerdict === 'REJECT' && <span className="admin-status-badge banned">Ai reject</span>}
                                                            {aiVerdict === 'PENDING' && <span className="admin-status-badge pending">Ai analyzing...</span>}
                                                        </td>
                                                        <td data-label="Details">
                                                            <div style={{ fontSize: '0.8rem' }}>{new Date(bug.created_at).toLocaleString()}</div>
                                                            {bug.recording_url && <span style={{ color: 'var(--blue)', fontSize: '0.75rem' }}>📹 Video Attached</span>}
                                                        </td>
                                                        <td data-label="Action">
                                                            <div style={{ display: 'flex', gap: '8px' }}>
                                                                <button
                                                                    className="admin-ghost-icon-btn"
                                                                    title="Slow Analysis/Logs"
                                                                    onClick={() => {
                                                                        setTerminalBugId(bug.id);
                                                                        setTerminalLogs('');
                                                                    }}
                                                                >
                                                                    <Activity size={14} color="#60a5fa" />
                                                                </button>
                                                                <button
                                                                    className="admin-ghost-icon-btn"
                                                                    disabled={rerunningBugId === bug.id}
                                                                    onClick={async () => {
                                                                        if (!window.confirm(`Re-run AI analysis for bug #${bug.id}? This will delete the existing analysis and start fresh.`)) return;
                                                                        setRerunningBugId(bug.id);
                                                                        try {
                                                                            await apiClient.rerunAnalysis(bug.id);
                                                                            showToast('🔄 AI re-analysis started. Refresh in 30-60s.');
                                                                            bug.ai_analysis = null;
                                                                            bug.ai_admin_context = null;
                                                                            loadPendingBugs();
                                                                        } catch (err) {
                                                                            showToast('Re-run failed: ' + err.message, 'error');
                                                                        } finally {
                                                                            setRerunningBugId(null);
                                                                        }
                                                                    }}
                                                                    title="Re-run AI Analysis"
                                                                >
                                                                    {rerunningBugId === bug.id ? <div className="spinner-sm" /> : <RefreshCw size={14} color="#f59e0b" />}
                                                                </button>
                                                                <button
                                                                    className="admin-ghost-icon-btn"
                                                                    disabled={approvingBugId === bug.id}
                                                                    onClick={async () => {
                                                                        setApprovingBugId(bug.id);
                                                                        try {
                                                                            await apiClient.adminApproveBug(bug.id, 'approved', 'Approved by admin');
                                                                            showToast('Bug approved');
                                                                            loadPendingBugs();
                                                                        } catch (err) {
                                                                            showToast('Failed: ' + err.message, 'error');
                                                                        } finally {
                                                                            setApprovingBugId(null);
                                                                        }
                                                                    }}
                                                                    title="Approve"
                                                                >
                                                                    {approvingBugId === bug.id ? <div className="spinner-sm" /> : <Check size={14} />}
                                                                </button>
                                                                <button
                                                                    className="admin-ghost-icon-btn"
                                                                    disabled={approvingBugId === bug.id}
                                                                    onClick={async () => {
                                                                        const reason = window.prompt("Reason for rejection:");
                                                                        if (reason === null) return;
                                                                        setApprovingBugId(bug.id);
                                                                        try {
                                                                            await apiClient.adminApproveBug(bug.id, 'rejected', reason);
                                                                            showToast('Bug rejected');
                                                                            loadPendingBugs();
                                                                        } catch (err) {
                                                                            showToast('Failed: ' + err.message, 'error');
                                                                        } finally {
                                                                            setApprovingBugId(null);
                                                                        }
                                                                    }}
                                                                    title="Reject"
                                                                >
                                                                    <X size={14} />
                                                                </button>
                                                                <button
                                                                    className="admin-ghost-icon-btn delete"
                                                                    onClick={() => handleDeleteBug(bug.id)}
                                                                    title="Delete Bug"
                                                                >
                                                                    <Trash size={14} />
                                                                </button>
                                                            </div>
                                                        </td>
                                                    </tr>
                                                    {isExpanded && (
                                                        <tr className="admin-expanded-row">
                                                            <td colSpan="5">
                                                                <div className="admin-expanded-content">
                                                                    <div className="admin-expanded-grid">
                                                                        {/* Left: Video Player */}
                                                                        <div>
                                                                            <h4 className="admin-expanded-title">
                                                                                <Smartphone size={14} /> Bug Recording
                                                                            </h4>
                                                                            {bug.recording_url ? (
                                                                                <div className="admin-video-container">
                                                                                    <BlobVideoPlayer src={bug.recording_url} />
                                                                                </div>
                                                                            ) : (
                                                                                <div className="empty-state" style={{ padding: '40px' }}>
                                                                                    No video recording available.
                                                                                </div>
                                                                            )}
                                                                            <div style={{ marginTop: '16px' }}>
                                                                                <div className="admin-stat-label" style={{ marginBottom: '4px' }}>Device Info:</div>
                                                                                <div style={{ fontSize: '0.85rem', color: '#fff' }}>{bug.device_info || 'Unknown device'}</div>
                                                                            </div>
                                                                        </div>

                                                                        {/* Right: Info & AI */}
                                                                        <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
                                                                            <div>
                                                                                <h4 className="admin-expanded-title" style={{ color: 'var(--saffron)' }}>📋 Instructions & Feedback</h4>
                                                                                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                                                                                    <div>
                                                                                        <div className="admin-stat-label" style={{ marginBottom: '4px' }}>Test Instructions</div>
                                                                                        <div className="admin-instructions-box" style={{ borderLeft: '3px solid var(--saffron)' }}>
                                                                                            {bug.test_instructions || 'No instructions provided for this test.'}
                                                                                        </div>
                                                                                    </div>
                                                                                    <div>
                                                                                        <div className="admin-stat-label" style={{ marginBottom: '4px' }}>Tester Feedback</div>
                                                                                        <div className="admin-instructions-box" style={{ borderLeft: '3px solid #ccc' }}>
                                                                                            {bug.bug_description}
                                                                                        </div>
                                                                                    </div>
                                                                                </div>
                                                                            </div>

                                                                            <div>
                                                                                <h4 className="admin-expanded-title" style={{ color: '#60a5fa' }}>
                                                                                    <Shield size={14} /> AI Vision Verdict (Gemini)
                                                                                </h4>
                                                                                {bug.ai_analysis ? (
                                                                                    <div className="admin-ai-analysis-box">
                                                                                        <div style={{ whiteSpace: 'pre-wrap' }}>
                                                                                            {bug.ai_analysis}
                                                                                        </div>

                                                                                        {bug.ai_admin_context && (
                                                                                            <div style={{
                                                                                                marginTop: '20px',
                                                                                                paddingTop: '20px',
                                                                                                borderTop: '1px dashed rgba(96, 165, 250, 0.3)',
                                                                                                color: '#fbbf24'
                                                                                            }}>
                                                                                                <h5 style={{ marginBottom: '8px', display: 'flex', alignItems: 'center', gap: '8px', fontSize: '12px' }}>
                                                                                                    <Shield size={12} /> INTERNAL ADMIN CONTEXT
                                                                                                </h5>
                                                                                                <div style={{ whiteSpace: 'pre-wrap', fontStyle: 'italic' }}>
                                                                                                    {bug.ai_admin_context}
                                                                                                </div>
                                                                                            </div>
                                                                                        )}

                                                                                        <div style={{ marginTop: '16px', paddingTop: '12px', borderTop: '1px solid rgba(255,255,255,0.08)', display: 'flex', justifyContent: 'flex-end' }}>
                                                                                            <button
                                                                                                className="admin-btn admin-btn-outline"
                                                                                                disabled={rerunningBugId === bug.id}
                                                                                                style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '0.8rem' }}
                                                                                                onClick={async () => {
                                                                                                    if (!window.confirm(`Re-run AI analysis for bug #${bug.id}? This will delete the current analysis and generate a new one.`)) return;
                                                                                                    setRerunningBugId(bug.id);
                                                                                                    try {
                                                                                                        await apiClient.rerunAnalysis(bug.id);
                                                                                                        showToast('🔄 AI re-analysis started. Refresh in 30-60s.');
                                                                                                        bug.ai_analysis = null;
                                                                                                        bug.ai_admin_context = null;
                                                                                                        loadPendingBugs();
                                                                                                    } catch (err) {
                                                                                                        showToast('Re-run failed: ' + err.message, 'error');
                                                                                                    } finally {
                                                                                                        setRerunningBugId(null);
                                                                                                    }
                                                                                                }}
                                                                                            >
                                                                                                {rerunningBugId === bug.id ? <div className="spinner-sm" /> : <RefreshCw size={13} />}
                                                                                                Re-run AI Analysis
                                                                                            </button>
                                                                                        </div>
                                                                                    </div>
                                                                                ) : (
                                                                                    <div className="empty-state" style={{ padding: '30px', border: '1px dashed rgba(255,255,255,0.1)' }}>
                                                                                        <div className="spinner" style={{ margin: '0 auto 12px' }} />
                                                                                        <p>Gemini is currently analyzing this recording...</p>
                                                                                    </div>
                                                                                )}
                                                                            </div>
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                            </td>
                                                        </tr>
                                                    )}
                                                </React.Fragment>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </>
                )}
            </div>

            {/* BUDGET MODAL */}
            {editingBudget && createPortal(
                <div className="admin-modal-overlay" onClick={() => setEditingBudget(null)}>
                    <div className="admin-budget-manager glass-card" onClick={e => e.stopPropagation()}>
                        <div className="admin-budget-manager-header">
                            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                <IndianRupee size={20} className="header-icon" />
                                <span>Pricing & Approval: <strong style={{ color: 'var(--saffron)' }}>{editingBudget.app_name}</strong></span>
                            </div>
                            <button className="admin-modal-close" onClick={() => setEditingBudget(null)}>
                                <X size={18} />
                            </button>
                        </div>

                        <div className="admin-budget-manager-grid">
                            <div className="admin-budget-field">
                                <label>Company Budget (Total ₹)</label>
                                <div className="admin-budget-input-wrapper">
                                    <IndianRupee size={16} />
                                    <input
                                        type="number"
                                        value={editingBudget.total_budget}
                                        onChange={(e) => {
                                            const val = e.target.value;
                                            const numVal = parseFloat(val) || 0;
                                            const totalTests = (editingBudget.tester_quota || 20) * (editingBudget.testing_iterations || 1);
                                            setEditingBudget({
                                                ...editingBudget,
                                                total_budget: val,
                                                price_paid: (numVal / totalTests).toFixed(2)
                                            });
                                        }}
                                    />
                                </div>
                                <div className="admin-field-hint">Total amount company will pay</div>
                            </div>

                            <div className="admin-budget-field">
                                <label>Gross Per Test (₹)</label>
                                <div className="admin-budget-input-wrapper">
                                    <IndianRupee size={16} />
                                    <input
                                        type="number"
                                        value={editingBudget.price_paid}
                                        onChange={(e) => {
                                            const val = e.target.value;
                                            const numVal = parseFloat(val) || 0;
                                            const totalTests = (editingBudget.tester_quota || 20) * (editingBudget.testing_iterations || 1);
                                            setEditingBudget({
                                                ...editingBudget,
                                                price_paid: val,
                                                total_budget: (numVal * totalTests).toFixed(0)
                                            });
                                        }}
                                    />
                                </div>
                                <div className="admin-field-hint">Company budget per session</div>
                            </div>
                        </div>

                        <div className="admin-payout-breakdown">
                            <div className="payout-row">
                                <span className="payout-label">Company Gross Per Test</span>
                                <span className="payout-value">₹{Number(editingBudget.price_paid || 0).toLocaleString()}</span>
                            </div>
                            <div className="payout-row highlight-cut">
                                <span className="payout-label">Admin Cut (70%)</span>
                                <span className="payout-value">- ₹{Number((editingBudget.price_paid || 0) * 0.7).toLocaleString()}</span>
                            </div>
                            <div className="payout-row highlight-payout">
                                <span className="payout-label">Tester Income (30%)</span>
                                <span className="payout-value">₹{Number((editingBudget.price_paid || 0) * 0.3).toLocaleString()}</span>
                            </div>
                        </div>

                        <div className="admin-budget-breakdown">
                            <div className="admin-breakdown-item">
                                <span className="label">Testers</span>
                                <span className="value">{editingBudget.tester_quota}</span>
                            </div>
                            <div className="admin-breakdown-sep">×</div>
                            <div className="admin-breakdown-item">
                                <span className="label">Iterations</span>
                                <span className="value">{editingBudget.testing_iterations}</span>
                            </div>
                            <div className="admin-breakdown-sep">=</div>
                            <div className="admin-breakdown-item highlight">
                                <span className="label">Total Tests</span>
                                <span className="value">{editingBudget.tester_quota * editingBudget.testing_iterations}</span>
                            </div>
                        </div>

                        <div className="admin-budget-manager-actions">
                            <button
                                className="admin-action-btn ban"
                                onClick={() => setEditingBudget(null)}
                                disabled={savingBudget}
                                style={{ flex: 1 }}
                            >
                                <X size={14} /> Cancel
                            </button>

                            {(editingBudget.status === 'pending-approval' || editingBudget.status === 'pending') ? (
                                <div style={{ display: 'flex', gap: '8px', flex: 2 }}>
                                    <button
                                        className="admin-action-btn delete"
                                        disabled={savingBudget}
                                        style={{ flex: 1 }}
                                        onClick={async () => {
                                            const reason = window.prompt("Reason for declining this test:");
                                            if (reason === null) return;
                                            console.log('Approve & Save clicked for:', editingBudget);
                                            setSavingBudget(true);
                                            try {
                                                await apiClient.updateTestStatus(editingBudget.id, 'rejected', reason);
                                                showToast(`❌ Test Declined: ${editingBudget.app_name}`);
                                                setEditingBudget(null);
                                                await loadAdminTests();
                                            } catch (err) {
                                                showToast('Failed to decline: ' + err.message, 'error');
                                            } finally {
                                                setSavingBudget(false);
                                            }
                                        }}
                                    >
                                        {savingBudget ? <div className="spinner-sm" /> : <><X size={14} /> Decline</>}
                                    </button>
                                    <button
                                        className="admin-action-btn unban"
                                        disabled={savingBudget}
                                        style={{ flex: 2 }}
                                        onClick={async () => {
                                            console.log('Approve & Save clicked for:', editingBudget);
                                            setSavingBudget(true);
                                            try {
                                                // 1. Update budget
                                                await apiClient.adminUpdateTestBudget(
                                                    editingBudget.id,
                                                    parseFloat(editingBudget.total_budget),
                                                    parseFloat(editingBudget.price_paid)
                                                );
                                                // 2. Approve test
                                                await apiClient.updateTestStatus(editingBudget.id, 'approved');

                                                showToast(`✅ Test Approved with Price ₹${editingBudget.price_paid}/test`);
                                                setEditingBudget(null);
                                                await loadAdminTests();
                                            } catch (err) {
                                                showToast('Failed to approve: ' + err.message, 'error');
                                            } finally {
                                                setSavingBudget(false);
                                            }
                                        }}
                                    >
                                        {savingBudget ? <div className="spinner-sm" /> : <><Check size={14} /> Approve & Save</>}
                                    </button>
                                </div>
                            ) : (
                                <button
                                    className="admin-action-btn unban"
                                    disabled={savingBudget}
                                    style={{ flex: 2 }}
                                    onClick={async () => {
                                        console.log('Approve & Save clicked for:', editingBudget);
                                        setSavingBudget(true);
                                        try {
                                            await apiClient.adminUpdateTestBudget(
                                                editingBudget.id,
                                                parseFloat(editingBudget.total_budget),
                                                parseFloat(editingBudget.price_paid)
                                            );
                                            showToast('Budget updated successfully');
                                            setEditingBudget(null);
                                            await loadAdminTests();
                                        } catch (err) {
                                            showToast('Failed to update: ' + err.message, 'error');
                                        } finally {
                                            setSavingBudget(false);
                                        }
                                    }}
                                >
                                    {savingBudget ? <div className="spinner-sm" /> : <><RefreshCw size={14} /> Update Price Details</>}
                                </button>
                            )}
                        </div>
                    </div>
                </div>,
                document.body
            )}

            {/* ASSIGN TESTERS MODAL */}
            {assignModalOpen && assignTestContext && createPortal(
                <div className="admin-modal-overlay" onClick={() => setAssignModalOpen(false)}>
                    <div className="admin-modal-content glass-card" onClick={e => e.stopPropagation()}>
                        <div className="admin-modal-header">
                            <h3>Assign Testers to {assignTestContext.app_name}</h3>
                            <button className="admin-modal-close" onClick={() => setAssignModalOpen(false)}><X size={20} /></button>
                        </div>

                        <div style={{ marginBottom: 20, fontSize: '0.9rem', color: 'rgba(255,255,255,0.5)', display: 'flex', justifyContent: 'space-between' }}>
                            <span>Target Company: <strong style={{ color: '#fff' }}>{assignTestContext.company_name}</strong></span>
                            <span>Quota: <strong style={{ color: '#fff' }}>{assignTestContext.tester_count || 0} / {assignTestContext.tester_quota || 20}</strong></span>
                        </div>

                        <div className="admin-tester-search-wrap">
                            <Search className="admin-tester-search-icon" size={18} />
                            <input
                                type="text"
                                className="admin-tester-search-input"
                                placeholder="Search by name, email, or device..."
                                value={assignSearch}
                                onChange={e => setAssignSearch(e.target.value)}
                            />
                        </div>

                        <div style={{ display: 'flex', gap: 10, marginBottom: 15 }}>
                            <button
                                className="admin-filter-tab active"
                                style={{ fontSize: 11 }}
                                onClick={() => {
                                    const filtered = testers.filter(t => !t.is_banned);
                                    setAssignTargetTesters(filtered.map(t => t.id));
                                }}
                            >
                                Select All Active
                            </button>
                            <button
                                className="admin-filter-tab"
                                style={{ fontSize: 11 }}
                                onClick={() => setAssignTargetTesters([])}
                            >
                                Deselect All
                            </button>
                        </div>

                        <div className="admin-tester-list">
                            {testers
                                .filter(t => !t.is_banned)
                                .filter(t => {
                                    if (!assignSearch) return true;
                                    const s = assignSearch.toLowerCase();
                                    return t.full_name?.toLowerCase().includes(s) ||
                                        t.email?.toLowerCase().includes(s) ||
                                        t.device_model?.toLowerCase().includes(s);
                                })
                                .map(t => (
                                    <div
                                        key={t.id}
                                        className="admin-tester-item"
                                        onClick={() => {
                                            if (assignTargetTesters.includes(t.id)) {
                                                setAssignTargetTesters(prev => prev.filter(id => id !== t.id));
                                            } else {
                                                setAssignTargetTesters(prev => [...prev, t.id]);
                                            }
                                        }}
                                    >
                                        <input
                                            type="checkbox"
                                            className="admin-tester-item-checkbox"
                                            checked={assignTargetTesters.includes(t.id)}
                                            onChange={() => { }} // Handled by parent div
                                        />
                                        <div style={{ flex: 1 }}>
                                            <div className="admin-tester-name">{t.full_name}</div>
                                            <div className="admin-tester-email">{t.device_model} • {t.city}</div>
                                        </div>
                                    </div>
                                ))
                            }
                        </div>

                        <div className="admin-modal-footer">
                            <button className="admin-btn admin-btn-outline" onClick={() => setAssignModalOpen(false)}>Cancel</button>
                            <button
                                className="admin-btn admin-btn-primary"
                                disabled={assignTargetTesters.length === 0 || assigningLoading}
                                onClick={async () => {
                                    try {
                                        setAssigningLoading(true);
                                        // Use batch if available, else loop
                                        for (const testerId of assignTargetTesters) {
                                            await apiClient.adminAssignTester(assignTestContext.id, testerId);
                                        }
                                        showToast(`✅ Successfully assigned ${assignTargetTesters.length} testers`);
                                        setAssignModalOpen(false);
                                        await loadAdminTests();
                                    } catch (err) {
                                        showToast('Assignment Error: ' + err.message, 'error');
                                    } finally {
                                        setAssigningLoading(false);
                                    }
                                }}
                            >
                                {assigningLoading ? 'Assigning...' : `Assign ${assignTargetTesters.length} Testers`}
                            </button>
                        </div>
                    </div>
                </div>,
                document.body
            )}

            {/* AI TERMINAL MODAL */}
            {terminalBugId && createPortal(
                <div className="admin-modal-overlay" onClick={() => setTerminalBugId(null)}>
                    <div className="admin-modal-content terminal-modal glass-card" onClick={e => e.stopPropagation()}>
                        <div className="admin-modal-header">
                            <h3 style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                <Activity size={18} color="#60a5fa" />
                                AI Analysis Terminal (Bug #{terminalBugId})
                            </h3>
                            <button className="admin-modal-close" onClick={() => setTerminalBugId(null)}>
                                <X size={20} />
                            </button>
                        </div>

                        <div className="terminal-container">
                            <div className="terminal-screen">
                                {terminalLogs ? (
                                    <pre>{terminalLogs}</pre>
                                ) : (
                                    <div className="terminal-empty">
                                        <div className="spinner-sm" />
                                        <span>Initializing terminal session...</span>
                                    </div>
                                )}
                            </div>
                        </div>

                        <div className="admin-modal-footer">
                            <div style={{ flex: 1, fontSize: '0.8rem', opacity: 0.6 }}>
                                {terminalLoading ? '🔄 Polling database for new logs...' : '✅ Terminal idle'}
                            </div>
                            <button
                                className="admin-btn admin-btn-outline"
                                onClick={() => setTerminalBugId(null)}
                            >
                                Close Terminal
                            </button>
                            <button
                                className="admin-btn admin-btn-primary"
                                onClick={async () => {
                                    try {
                                        setTerminalLogs('Terminal: Starting manual re-analysis...\n');
                                        await apiClient.analyzeWithAI(terminalBugId);
                                        showToast('🚀 AI Analysis triggered');
                                    } catch (err) {
                                        setTerminalLogs(prev => prev + `Error starting analysis: ${err.message}\n`);
                                    }
                                }}
                            >
                                Restart Analysis
                            </button>
                        </div>
                    </div>
                </div>,
                document.body
            )}
        </div>
    );
}
